import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OrganizationProvider } from './context/OrganizationContext';
import { ProjectProvider } from './context/ProjectContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import Structure from './components/workflows/Structure';
import { UserProvider } from './context/UserContext';
import ConnectionDetails from './routes/planning/ConnectionDetails';
import ConnectionCallback from './routes/planning/ConnectionCallback';
import InvitationChecker from './components/InvitationChecker';

import Media from './components/documents/media/Media';
import FindImage from './routes/find-image/FindImage';
import Products from './routes/products/Products';
import Documents from './components/documents/Documents';
import ViewTable from './components/documents/document/ViewTable';
import ViewDocument from './components/documents/document/ViewDocument';
import WebsiteContent from './components/documents/website/WebsiteContent';
import SocialContent from './components/documents/social/SocialContent';
import BlogContent from './components/documents/blog/BlogContent';

// Lazy load route components
const Login = React.lazy(() => import('./routes/auth/Login'));
const Signup = React.lazy(() => import('./routes/auth/Signup'));
const Dashboard = React.lazy(() => import('./routes/dashboard/Dashboard'));
const Strategy = React.lazy(() => import('./routes/strategy/Strategy'));
const Connections = React.lazy(() => import('./routes/planning/Connections'));
const Reports = React.lazy(() => import('./routes/reports/Reports'));
const Calendar = React.lazy(() => import('./components/documents/Calendar'));
const Details = React.lazy(() => import('./routes/settings/Details'));
const EditStrategy = React.lazy(() => import('./routes/strategy/EditStrategy'));
const Profile = React.lazy(() => import('./routes/project/Profile'));
const Audience = React.lazy(() => import('./routes/project/Audience'));
const AddAudience = React.lazy(() => import('./routes/project/AddAudience'));
const EditAudience = React.lazy(() => import('./routes/project/EditAudience'));
const WorkflowsList = React.lazy(() => import('./components/workflows/WorkflowsList'));
const WorkflowView = React.lazy(() => import('./components/workflows/WorkflowView'));
const Home = React.lazy(() => import('./routes/home/Home'));
const Pricing = React.lazy(() => import('./routes/pricing/Pricing'));
const Usage = React.lazy(() => import('./routes/settings/Usage'));
const Administration = React.lazy(() => import('./routes/admin/Administration'));
const AcceptInvite = React.lazy(() => import('./routes/auth/AcceptInvite'));

function AppRoutes() {
  const { loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public routes with individual Suspense boundaries */}
      <Route
        path="/"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <Home />
          </Suspense>
        }
      />
      <Route
        path="/login"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <Login />
          </Suspense>
        }
      />
      <Route
        path="/signup"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <Signup />
          </Suspense>
        }
      />
      <Route
        path="/pricing"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <Pricing />
          </Suspense>
        }
      />
      <Route
        path="/accept-invite"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <AcceptInvite />
          </Suspense>
        }
      />

      {/* Protected routes wrapped in a single Suspense boundary */}
      {user ? (
        <Route
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Layout />
            </Suspense>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/strategy" element={<Strategy />} />
          <Route path="/connections" element={<Connections />} />
          <Route path="/connections/:id" element={<ConnectionDetails />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/content/calendar" element={<Calendar />} />
          <Route path="/settings/details" element={<Details />} />
          <Route path="/strategy/:id/edit" element={<EditStrategy />} />
          <Route path="/project/profile" element={<Profile />} />
          <Route path="/project/audience" element={<Audience />} />
          <Route path="/project/audience/add" element={<AddAudience />} />
          <Route path="/project/audience/:id/edit" element={<EditAudience />} />
          <Route path="/structure" element={<Structure />} />
          <Route path="/workflows" element={<WorkflowsList />} />
          <Route path="/workflows/new" element={<WorkflowView />} />
          <Route path="/workflows/:id" element={<WorkflowView />} />
          <Route path="/connections/callback" element={<ConnectionCallback />} />
          <Route path="/content/create/social" element={
            <SocialContent isEditing={false} />
          } />
          <Route path="/content/social/:id" element={
            <SocialContent isEditing={true} />
          } />
          <Route path="/content/create/blog" element={
            <BlogContent isEditing={false} />
          } />
          <Route path="/content/blog/:id" element={
            <BlogContent isEditing={true} />
          } />
          <Route path="/content/media" element={<Media />} />
          <Route path="/find-image" element={<FindImage />} />
          <Route path="/products" element={<Products />} />
          <Route path="/content/documents" element={<Documents />} />
          <Route path="/content/tables/:id" element={<ViewTable />} />
          <Route path="/content/documents/:id" element={<ViewDocument />} />
          <Route path="/content/website/:id" element={<WebsiteContent />} />
          <Route path="/settings/usage" element={<Usage />} />
          <Route path="/admin" element={<Administration />} />
        </Route>
      ) : null}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <UserProvider>
        <BrowserRouter>
          <AuthProvider>
            <ToastProvider>
              <ConfirmProvider>
                <OrganizationProvider>
                  <ProjectProvider>
                    <InvitationChecker>
                      <Suspense fallback={<LoadingSpinner />}>
                        <AppRoutes />
                      </Suspense>
                    </InvitationChecker>
                  </ProjectProvider>
                </OrganizationProvider>
              </ConfirmProvider>
            </ToastProvider>
          </AuthProvider>
        </BrowserRouter>
      </UserProvider>
    </ThemeProvider>
  );
} 